import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { Badge } from '../badge';
import { Typography } from '../typography';
import { useStore } from '../../context';
import { rolesLabel, TABLE } from '../../const';
import { LanguageSelect } from '../select';
import { IconButton } from '../button';
import { smallScreenSize, useIsSmallScreen, useNotificationsQuery } from '../../hooks';
import { showToRoles } from '../../utils';
import { Logo } from '../logo';
import { tutorialsModal } from '../modal';
import { OverviewInputs } from './OverviewInputs';

const AppbarBase = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  grid-column-gap: 0.4rem;
  justify-content: end;
  align-items: center;

  width: 100%;
  height: 6.8rem;

  background-color: var(--white);
  border-bottom: 0.1rem solid var(--gray-50);
  padding: 0 2.4rem;

  @media (max-width: ${smallScreenSize}px) {
    grid-template-columns: 1fr;
    padding: 0 1.2rem;
  }

  @media print {
    display: none;
  }
`;

export const AppbarWrapperInputs = styled.div``;

export const AppbarInputs = styled.div`
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  grid-column-gap: var(--spacing-6);
  align-items: center;
  width: max-content;
`;

const NotificationIconButton = styled(IconButton)`
  & > svg.MuiSvgIcon-root {
    transform-origin: 50% 10%;

    @keyframes ring {
      0% {
        transform: rotate(0deg);
      }
      4% {
        transform: rotate(-15deg);
      }
      8% {
        transform: rotate(10deg);
      }
      12% {
        transform: rotate(-8deg);
      }
      16% {
        transform: rotate(6deg);
      }
      20% {
        transform: rotate(-4deg);
      }
      24% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(0deg);
      }
    }
  }

  &[data-animated='true'] > svg.MuiSvgIcon-root {
    animation: ring 4s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite;
  }
`;

const MenuIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    @media (max-width: ${smallScreenSize}px) {
      margin-right: 1.2rem;
    }
  }
`;

export const Appbar = ({
  handleChangeDrawer,
}: {
  handleChangeDrawer: (drawer: string) => void;
}) => {
  const {
    state: {
      refreshPage,
      user: { data: user },
    },
  } = useStore();
  const isSmallScreen = useIsSmallScreen();
  const { data = { totalCount: 0 } } = useNotificationsQuery({
    user,
    take: TABLE.PAGE_MAX_SIZE,
    skip: 0,
    isRead: false,
  });
  const userInfoText = useMemo(
    () =>
      isSmallScreen ? null : (
        <>
          {rolesLabel[user.type]}
          {(user.firstName || user.lastName) && ':'}
          {user.firstName && ` ${user.firstName}`}
          {user.lastName && ` ${user.lastName}`}
        </>
      ),
    [isSmallScreen],
  );

  const handleTutorialClick = () => {
    tutorialsModal.open();
  };

  return (
    <AppbarBase id={'appbar'}>
      {isSmallScreen && <Logo isOpen={true} isInvert={true} />}
      {!isSmallScreen && (
        <AppbarWrapperInputs>
          <OverviewInputs />
        </AppbarWrapperInputs>
      )}
      <Typography variant={'textM'} color={'--gray-80'} capitalized align="right">
        {userInfoText}
      </Typography>
      <LanguageSelect isButtonVariant />
      <IconButton iconColor="--gray-80" onClick={handleTutorialClick}>
        <HelpOutlineIcon />
      </IconButton>
      {showToRoles(['SUPER_ADMIN'], user) && (
        <Badge
          key={refreshPage}
          badgeContent={data.totalCount}
          data-size={'small'}
          overlap={'circular'}
        >
          <NotificationIconButton
            iconColor="--gray-80"
            data-animated={!!data.totalCount}
            onClick={() => handleChangeDrawer('notifications')}
          >
            <NotificationsIcon />
          </NotificationIconButton>
        </Badge>
      )}
      {isSmallScreen && (
        <MenuIconButton
          id="appbar-menu-icon"
          iconColor="--gray-80"
          onClick={() => handleChangeDrawer('dashboard')}
        >
          <MenuIcon />
        </MenuIconButton>
      )}
    </AppbarBase>
  );
};
